import * as React from 'react';
import Dropdown from 'antd/lib/dropdown';
import Avatar from 'antd/lib/avatar';
import Flex from 'antd/lib/flex';
import Text from 'antd/lib/typography/Text';
import { AppPlatform } from '../../../types';
import { Destination } from '../../../types/destinations';
import { getNavDropdownMenuStyle } from '../../styles/globalStyles';
import { useSidebarColors } from '../../styles/themeMode';
import WorkflowsPlatformIcon from '../../icons/WorkflowsPlatformIcon';
import { DropdownButtonContent } from './DropdownButtonContent';
import { HoverableNavItem } from '../shared/HoverableNavItem';
import { getMdmPlatformOverride } from '../../../helpers/temporaryMdmDisplayOverride';

interface PlatformMenuItem {
    key: string;
    label: React.ReactNode;
    isDisabled?: boolean;
}

interface PlatformDropdownProps {
    orgId: string;
    app: { appId?: string } | null | undefined;
    primaryPlatform: AppPlatform;
    platformDisplayText: string;
    showPlatformDropdown: boolean;
    platformMenuItems: PlatformMenuItem[];
    selectedKey?: string;
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    onPlatformSelect: (key: string) => void;
    onSettingsClick?: () => void;
    showAppImage?: boolean;
    showAppName?: boolean;
    appImageUrl?: string;
    appName?: string;
    destinations?: Destination[];
}

export const PlatformDropdown: React.FC<PlatformDropdownProps> = ({
    orgId,
    app,
    primaryPlatform,
    platformDisplayText,
    showPlatformDropdown,
    platformMenuItems,
    selectedKey,
    isOpen,
    onOpenChange,
    onPlatformSelect,
    showAppImage,
    showAppName,
    appImageUrl,
    appName,
    destinations,
}) => {
    const sc = useSidebarColors();
    const mdmOverride = getMdmPlatformOverride(orgId, app?.appId);
    const resolvedDisplayText = mdmOverride?.label ?? platformDisplayText;

    const icon =
        mdmOverride ? mdmOverride.icon(28)
        : showAppImage ?
            <Avatar
                shape="square"
                size={28}
                src={appImageUrl}
                style={{ borderRadius: 6, border: 'none', flexShrink: 0 }}
            >
                {appName?.charAt(0).toUpperCase()}
            </Avatar>
        :   <WorkflowsPlatformIcon platform={primaryPlatform} width={28} height={28} />;

    const text =
        showAppName ?
            <span>
                {appName}
                <Text style={{ color: sc.text, fontWeight: 400 }}> | {resolvedDisplayText}</Text>
            </span>
        :   resolvedDisplayText;

    const content = (
        <DropdownButtonContent
            icon={icon}
            text={text}
            showCaret={showPlatformDropdown}
            cursor={showPlatformDropdown ? 'pointer' : 'default'}
            destinations={destinations}
        />
    );

    if (!showPlatformDropdown) {
        return content;
    }

    const dropdownContent = (
        <Flex
            vertical
            style={{
                ...getNavDropdownMenuStyle(sc),
                padding: 0,
                minWidth: '25rem',
                maxHeight: '40vh',
                overflowY: 'auto',
            }}
        >
            {platformMenuItems.map((item) => (
                <HoverableNavItem
                    key={item.key}
                    isSelected={item.key === selectedKey}
                    isDisabled={item.isDisabled}
                    onClick={() => {
                        onPlatformSelect(item.key);
                        onOpenChange(false);
                    }}
                    style={{ padding: '8px 16px' }}
                >
                    {item.label}
                </HoverableNavItem>
            ))}
        </Flex>
    );

    return (
        <Dropdown open={isOpen} onOpenChange={onOpenChange} dropdownRender={() => dropdownContent} trigger={['click']}>
            <div>{content}</div>
        </Dropdown>
    );
};
